import { Grid } from '@mui/material';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  useCommunications,
  useDocumentReferences,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { DocumentList } from '../../patient/attachments/DocumentList';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { CommunicationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Communication';
import { CRS_NOTES_COLUMNS, NotesDisplayWithAdd } from 'src/components/NotesDisplay';
import { mapCommunicationsToNotesDisplay } from 'src/careflow/common/common-utils';
import { useState } from 'react';
import NoteAddForm from 'src/careflow/common/NoteAddForm';
import ExpandableCard from 'src/components/common/ExpandableCard';

function Documents({
  episodeOfCare,
  patient,
  i18n,
  configurations = {},
}: {
  episodeOfCare?: string;
  patient?: WrappedPatient | null;
  i18n: i18nFunction;
  configurations?: any;
}) {
  const { onlyPatientDocuments = false } = configurations;
  const [documentReferences, { refresh: refreshDocumentReference }] = useDocumentReferences({
    filter: {
      subject: patient?.id,
      ...(!onlyPatientDocuments && { encounter: episodeOfCare }),
      _sort: '-date',
    },
    autofetch: !!patient?.id && (onlyPatientDocuments || !!episodeOfCare),
  });

  const [communications, { create: createCommunication, refresh: refreshCommunications }] =
    useCommunications({
      filter: { subject: patient?.id, 'part-of': episodeOfCare },
      map: CommunicationWrapper,
      autofetch: !!patient?.id && !!episodeOfCare,
    });

  const [openNote, setOpenNote] = useState(false);

  const handleCreateCommunicationFhir = async (data: any) => {
    try {
      const payload = {
        ...data,
        partOf: [
          {
            reference: `EpisodeOfCare/${episodeOfCare}`,
          },
        ],
      };
      await createCommunication(payload);
      setOpenNote(false);
      refreshCommunications();
    } catch (err) {
      console.log('error on handleCreateCommunicationFhir', err);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ExpandableCard
          title={i18n('episodeOfCare.details.documents.attachments', 'crs')}
          openCollapseExternal={true}
        >
          <DocumentList
            patient={patient ?? null}
            documentReferences={documentReferences}
            showFilters={true}
            refreshDocumentReference={refreshDocumentReference}
            openCollapseExternal={true}
            handleUpdateResource={null}
            permissions={{
              isAllowedToAdd:
                Boolean(episodeOfCare) &&
                checkAclValidation({
                  acls: [crsAcls.CRS.EPISODE_OF_CARE.DOCUMENTS.ATTACHMENTS.ADD],
                }),
              isAllowedToEdit: checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.DOCUMENTS.ATTACHMENTS.EDIT],
              }),
            }}
            episodeOfCare={
              episodeOfCare ? { reference: `EpisodeOfCare/${episodeOfCare}` } : undefined
            }
          />
        </ExpandableCard>
      </Grid>

      <Grid item xs={12}>
        <ExpandableCard
          title={i18n('episodeOfCare.details.documents.notes', 'crs')}
          openCollapseExternal={true}
        >
          <NotesDisplayWithAdd
            data={mapCommunicationsToNotesDisplay(communications, [])}
            columns={CRS_NOTES_COLUMNS}
            titleButton={
              checkAclValidation({ acls: [crsAcls.CRS.EPISODE_OF_CARE.DOCUMENTS.NOTES.ADD] })
                ? i18n('episodeOfCare.details.documents.noteAdd', 'crs')
                : undefined
            }
            isAllowedToAdd={
              Boolean(episodeOfCare) &&
              checkAclValidation({
                acls: [crsAcls.CRS.EPISODE_OF_CARE.DOCUMENTS.NOTES.ADD],
              })
            }
            onAddNote={() => setOpenNote(true)}
            emptyMessage={i18n('episodeOfCare.details.documents.noNotes', 'crs')}
          />
        </ExpandableCard>
      </Grid>
      <NoteAddForm
        open={openNote}
        patient={patient ?? null}
        resource={[]}
        onCancel={() => setOpenNote(false)}
        handleCreate={handleCreateCommunicationFhir}
        typeNote="notes_patient"
      />
    </Grid>
  );
}

export default Documents;
